/**
 * VisionUploadSheet — pick a weekly-/homework-plan photo + the child it belongs
 * to (Fase 6). Pure form: it emits the chosen File + assignee selection; the
 * AssistantRoute does the upload (admin-scope-guarded) and starts the chat turn.
 *
 * "Hvem sin plan?" pre-selects the assignee the vision agent fills onto every
 * draft (the model never guesses names). Admin-only — the trigger is hidden
 * from non-admin sessions upstream.
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MemberAdminRow } from '@stell/shared';
import { BottomSheet } from '../../components/primitives';

export interface VisionSubmit {
  file: File;
  assigneeId: string | undefined;
  message: string;
}

interface VisionUploadSheetProps {
  open: boolean;
  onClose: () => void;
  members: MemberAdminRow[];
  onSubmit: (v: VisionSubmit) => void;
}

export function VisionUploadSheet({ open, onClose, members, onSubmit }: VisionUploadSheetProps) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [assigneeId, setAssigneeId] = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);

  const assignable = members.filter((m) => !m.archived_at && m.role !== 'admin');

  function submit() {
    if (!file) return;
    onSubmit({
      file,
      assigneeId: assigneeId || undefined,
      message: t('assistant.vision.defaultMessage'),
    });
    setFile(null);
    setAssigneeId('');
    onClose();
  }

  return (
    <BottomSheet open={open} onClose={onClose} title={t('assistant.vision.uploadTitle')}>
      <div className="space-y-4">
        <p className="text-sm text-ink-2">{t('assistant.vision.uploadHelp')}</p>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          data-testid="vision-file-input"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm text-ink file:mr-3 file:rounded-full file:border-0 file:bg-primary file:px-4 file:py-2 file:text-on-primary"
        />
        {file && <p className="text-xs text-ink-2" data-testid="vision-file-name">{file.name}</p>}

        <label className="block space-y-1">
          <span className="text-xs text-ink-2">{t('assistant.vision.whosePlan')}</span>
          <select
            data-testid="vision-assignee"
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            className="w-full rounded-md border border-card-line bg-raised px-3 py-2 text-base text-ink focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">{t('assistant.vision.assigneeNone')}</option>
            {assignable.map((m) => (
              <option key={m.id} value={m.id}>
                {m.short_name}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={submit}
          disabled={!file}
          data-testid="vision-submit"
          className="min-h-11 w-full rounded-full bg-primary text-on-primary text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:pointer-events-none transition-colors"
        >
          {t('assistant.vision.analyze')}
        </button>
      </div>
    </BottomSheet>
  );
}
